import React, { useState, useEffect } from "react";
import { Edit, useForm } from "@refinedev/antd";
import { useNavigation } from "@refinedev/core";
import { Form, Input, Select, Spin, message } from "antd";
import { USER_ONE_QUERY, UPDATE_USER_MUTATION, GET_APP_TEAMS_QUERY } from "./queries";
import { User } from "./interface";
import { gqlClient } from "../../helpers/gqlProvider";

const { Option } = Select;

interface AppTeam {
  id: string;
  name: string;
  description?: string;
}

export const EditUser: React.FC = () => {
  const { list } = useNavigation();
  const { formProps, saveButtonProps, query } = useForm<User>({
    resource: "users",
    action: "edit",
    meta: {
      gqlQuery: USER_ONE_QUERY,
      gqlMutation: UPDATE_USER_MUTATION,
    },
  });

  const record = query?.data?.data;

  const [appTeams, setAppTeams] = useState<AppTeam[]>([]);
  const [loadingAppTeams, setLoadingAppTeams] = useState(true);
  const [selectedAppTeams, setSelectedAppTeams] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (record?.appTeams?.length) {
      setSelectedAppTeams(record.appTeams.map(t => t.id));
    }
  }, [record?.appTeams]);

  useEffect(() => {
    const fetchAppTeams = async () => {
      try {
        setLoadingAppTeams(true);
        const result = await gqlClient.query(GET_APP_TEAMS_QUERY, {}).toPromise();
        if (result.data && result.data.getAppTeams) {
          setAppTeams(result.data.getAppTeams);
        }
      } catch (error) {
        console.error("Failed to fetch app teams:", error);
        message.error("Failed to load app teams");
      } finally {
        setLoadingAppTeams(false);
      }
    };

    fetchAppTeams();
  }, []);

  const handleSave = async () => {
    if (!record?.id) return;
    setSaving(true);
    try {
      const input = { appTeams: selectedAppTeams };
      const result = await gqlClient.mutation(UPDATE_USER_MUTATION, { id: record.id, input }).toPromise();
      if (result.error) throw result.error;
      message.success("User updated successfully");
      list("users");
    } catch (error) {
      message.error("Failed to update user: " + (error instanceof Error ? error.message : "Unknown error"));
    } finally {
      setSaving(false);
    }
  };

  const customizedSaveProps = {
    ...saveButtonProps,
    onClick: (e: React.MouseEvent) => {
      e.preventDefault();
      handleSave();
    },
    loading: saving,
  };

  return (
    <Edit saveButtonProps={customizedSaveProps} title="Edit User">
      <Form layout="vertical" {...formProps}>
        <Form.Item label="Email" name="email">
          <Input placeholder="Enter user email" disabled />
        </Form.Item>

        <Form.Item label="First Name" name="firstName">
          <Input placeholder="Enter first name" disabled />
        </Form.Item>

        <Form.Item label="Last Name" name="lastName">
          <Input placeholder="Enter last name" disabled />
        </Form.Item>

        <Form.Item label="App Teams">
          <Spin spinning={loadingAppTeams}>
            <Select
              mode="multiple"
              placeholder="Select app teams (optional)"
              disabled={loadingAppTeams}
              showSearch
              value={selectedAppTeams}
              onChange={(value) => setSelectedAppTeams(value)}
              optionFilterProp="children"
              filterOption={(input, option) =>
                (option?.children?.toString().toLowerCase() || '').includes(input.toLowerCase())
              }
            >
              {appTeams.map((team) => (
                <Option key={team.id} value={team.id}>
                  {team.name} {team.description && `(${team.description})`}
                </Option>
              ))}
            </Select>
          </Spin>
        </Form.Item>

        <Form.Item label="Organization Role" name="organizationalRole">
          <Input placeholder="Enter organization role (optional)" disabled />
        </Form.Item>
      </Form>
    </Edit>
  );
};